import { Link, NavLink } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import DarkModeToggle from "@/components/DarkModeToggle";
import PodcastSelector from "@/components/PodcastSelector";
import CreditsDisplay from "@/components/CreditsDisplay";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

export default function Header() {
  const { user, logout } = useAuth();
  const displayName = user?.display_name?.trim() || user?.email || "";
  const userInitial = displayName ? displayName.charAt(0).toUpperCase() : "";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const hamburgerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (hamburgerRef.current && !hamburgerRef.current.contains(event.target as Node)) {
        setIsHamburgerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setIsMenuOpen(false);
      setIsHamburgerOpen(false);
    }
  }, [user]);

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
  };

  const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
    `text-lg font-medium transition-colors ${
      isActive
        ? "text-primary-orange-dark"
        : "text-gray-900 dark:text-white hover:text-primary-orange-dark"
    }`;

  return (
    <header className={`sticky top-0 w-full ${theme.colors.cardCream} dark:bg-gray-800 border-b-3 border-gray-900 p-4 md:p-6 transition-colors duration-200 z-[60]`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-3">
          {/* Left side: Podcast selector - always visible */}
          <div className="flex-shrink-0 min-w-0">
            <PodcastSelector />
          </div>

          {/* Right side: Controls */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Mobile: Hamburger menu with everything */}
            <div className="relative lg:hidden" ref={hamburgerRef}>
              <button
                type="button"
                onClick={() => setIsHamburgerOpen(prev => !prev)}
                className={cn(
                  "flex items-center justify-center h-11 w-11 rounded-full border-2 border-gray-900 dark:border-gray-300",
                  "bg-white/90 dark:bg-gray-700/90",
                  theme.shadows.neubrutalism3,
                  "transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-primary-orange/30 dark:focus:ring-primary-orange-dark/30",
                )}
                aria-label="Menu"
              >
                {isHamburgerOpen ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6 text-gray-900 dark:text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6 text-gray-900 dark:text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
              {isHamburgerOpen && (
                <div className={cn(
                  "absolute right-0 mt-3 w-72 rounded-2xl border-2 border-gray-900 dark:border-gray-300",
                  "bg-white/95 dark:bg-gray-800/95",
                  theme.shadows.neubrutalismLg,
                  "overflow-hidden z-50",
                )}>
                  <nav className="flex flex-col">
                    <NavLink
                      to="/episodes"
                      onClick={() => setIsHamburgerOpen(false)}
                      className={({ isActive }) =>
                        `px-4 py-3 text-sm font-medium transition-colors ${
                          isActive
                            ? "text-primary-orange-dark bg-card-cream dark:bg-gray-700"
                            : "text-gray-900 dark:text-gray-100 hover:bg-card-cream dark:hover:bg-gray-700"
                        }`
                      }
                    >
                      Episodes
                    </NavLink>
                    <NavLink
                      to="/chat"
                      onClick={() => setIsHamburgerOpen(false)}
                      className={({ isActive }) =>
                        `px-4 py-3 text-sm font-medium transition-colors ${
                          isActive
                            ? "text-primary-orange-dark bg-card-cream dark:bg-gray-700"
                            : "text-gray-900 dark:text-gray-100 hover:bg-card-cream dark:hover:bg-gray-700"
                        }`
                      }
                    >
                      Chat
                    </NavLink>
                    <NavLink
                      to="/try"
                      onClick={() => setIsHamburgerOpen(false)}
                      className={({ isActive }) =>
                        `px-4 py-3 text-sm font-medium transition-colors ${
                          isActive
                            ? "text-primary-orange-dark bg-card-cream dark:bg-gray-700"
                            : "text-gray-900 dark:text-gray-100 hover:bg-card-cream dark:hover:bg-gray-700"
                        }`
                      }
                    >
                      Explore
                    </NavLink>
                    <Link
                      to="/podcasts/add"
                      onClick={() => setIsHamburgerOpen(false)}
                      className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-card-cream dark:hover:bg-gray-700 transition-colors"
                    >
                      Add Podcast
                    </Link>
                    {user && (
                      <>
                        <div className="px-4 py-3 border-t-2 border-gray-900/60 dark:border-gray-300/40">
                          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Signed in</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate" title={displayName}>
                            {displayName}
                          </p>
                        </div>
                        <NavLink
                          to="/profile"
                          onClick={() => setIsHamburgerOpen(false)}
                          className={({ isActive }) =>
                            `px-4 py-3 text-sm font-medium transition-colors ${
                              isActive
                                ? "text-primary-orange-dark bg-card-cream dark:bg-gray-700"
                                : "text-gray-900 dark:text-gray-100 hover:bg-card-cream dark:hover:bg-gray-700"
                            }`
                          }
                        >
                          Settings
                        </NavLink>
                        <NavLink
                          to="/billing"
                          onClick={() => setIsHamburgerOpen(false)}
                          className={({ isActive }) =>
                            `px-4 py-3 text-sm font-medium transition-colors ${
                              isActive
                                ? "text-primary-orange-dark bg-card-cream dark:bg-gray-700"
                                : "text-gray-900 dark:text-gray-100 hover:bg-card-cream dark:hover:bg-gray-700"
                            }`
                          }
                        >
                          Billing
                        </NavLink>
                      </>
                    )}
                    <div className="px-4 py-3 flex items-center justify-between border-t-2 border-gray-900/60 dark:border-gray-300/40">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Theme</span>
                      <DarkModeToggle />
                    </div>
                    {user && (
                      <div className="px-4 py-3 flex items-center justify-between border-t-2 border-gray-900/60 dark:border-gray-300/40">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Credits</span>
                        <CreditsDisplay />
                      </div>
                    )}
                    {user && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsHamburgerOpen(false);
                          handleLogout();
                        }}
                        className="px-4 py-3 text-left text-sm font-medium text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors border-t-2 border-gray-900/60 dark:border-gray-300/40"
                      >
                        Log out
                      </button>
                    )}
                  </nav>
                </div>
              )}
            </div>

            {/* Desktop nav - hidden on smaller screens, shows on lg+ */}
            <nav className="hidden lg:flex items-center gap-6 shrink-0">
              <NavLink to="/episodes" className={navLinkClasses} end>
                Episodes
              </NavLink>
              <NavLink to="/chat" className={navLinkClasses}>
                Chat
              </NavLink>
              <NavLink to="/try" className={navLinkClasses}>
                Explore
              </NavLink>
            </nav>
            
            {/* Add Podcast button - hidden on smaller screens, shows on lg+ */}
            <Link
              to="/podcasts/add"
              className={cn(
                theme.buttons.primary,
                "hidden lg:flex rounded-full px-5 py-2 text-base font-semibold leading-none min-h-[2.75rem] items-center justify-center whitespace-nowrap shrink-0",
              )}
            >
              Add Podcast
            </Link>
            
            {/* Desktop controls - hidden on smaller screens */}
            <div className="hidden lg:flex items-center gap-3 shrink-0">
              <DarkModeToggle />
              {user && <CreditsDisplay />}
            </div>
            
            {/* Profile dropdown - desktop only, hidden on mobile (in hamburger) */}
            {user && (
              <div className="hidden lg:block relative shrink-0" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setIsMenuOpen(prev => !prev)}
                  className={cn(
                    "flex items-center justify-center h-11 w-11 rounded-full border-2 border-gray-900 dark:border-gray-300",
                    "bg-white/90 dark:bg-gray-700/90 text-lg font-semibold text-gray-900 dark:text-white",
                    theme.shadows.neubrutalism3,
                    "transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-primary-orange/30 dark:focus:ring-primary-orange-dark/30",
                  )}
                  aria-haspopup="true"
                  aria-expanded={isMenuOpen}
                  title={displayName}
                >
                  {userInitial || "👤"}
                </button>
                {isMenuOpen && (
                  <div className={cn(
                    "absolute right-0 mt-3 w-48 rounded-2xl border-2 border-gray-900 dark:border-gray-300",
                    "bg-white/95 dark:bg-gray-800/95",
                    theme.shadows.neubrutalismLg,
                    "overflow-hidden z-50",
                  )}>
                    <div className="px-4 py-3 border-b-2 border-gray-900/60 dark:border-gray-300/40">
                      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Signed in</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate" title={displayName}>
                        {displayName}
                      </p>
                    </div>
                    <nav className="flex flex-col">
                      <NavLink
                        to="/profile"
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) =>
                          `px-4 py-3 text-sm font-medium transition-colors ${
                            isActive
                              ? "text-primary-orange-dark"
                              : "text-gray-900 dark:text-gray-100 hover:bg-card-cream dark:hover:bg-gray-700"
                          }`
                        }
                      >
                        Settings
                      </NavLink>
                      <NavLink
                        to="/billing"
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) =>
                          `px-4 py-3 text-sm font-medium transition-colors ${
                            isActive
                              ? "text-primary-orange-dark"
                              : "text-gray-900 dark:text-gray-100 hover:bg-card-cream dark:hover:bg-gray-700"
                          }`
                        }
                      >
                        Billing
                      </NavLink>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="px-4 py-3 text-left text-sm font-medium text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                      >
                        Log out
                      </button>
                    </nav>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
